import React, { useEffect, useState } from "react";
import { ZapOff, Zap } from "lucide-react";

/**
 * Global on/off switch for demo callouts. Persists in localStorage.
 * Mounted in the demo header so prospects can toggle the explanatory
 * overlays at any time.
 */
const STORAGE_KEY = "forgedops.demo.callouts";

function readPref() {
  if (typeof window === "undefined") return true;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === null ? true : v === "1";
}
function writePref(on) {
  try { window.localStorage.setItem(STORAGE_KEY, on ? "1" : "0"); } catch { /* */ }
  window.dispatchEvent(new CustomEvent("forgedops-callouts", { detail: on }));
}
export function useCallouts() {
  const [on, setOn] = useState(readPref);
  useEffect(() => {
    const handler = (e) => setOn(!!e.detail);
    window.addEventListener("forgedops-callouts", handler);
    return () => window.removeEventListener("forgedops-callouts", handler);
  }, []);
  return on;
}
export function CalloutsToggle({ className = "" }) {
  const [on, setOn] = useState(readPref);
  const toggle = () => {
    const nv = !on; setOn(nv); writePref(nv);
  };
  return (
    <button
      onClick={toggle}
      data-testid="callouts-toggle"
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] px-2.5 py-1.5 rounded border transition ${className}`}
      style={{
        background: on ? "rgba(255,106,0,0.14)" : "transparent",
        borderColor: on ? "rgba(255,106,0,0.4)" : "#2A2A2A",
        color: on ? "#FF6A00" : "#BDBDBD",
      }}
      title="Toggle demo annotations"
    >
      {on ? <Zap className="w-3 h-3" /> : <ZapOff className="w-3 h-3" />}
      Callouts {on ? "On" : "Off"}
    </button>
  );
}

/**
 * <DemoCallout> — wraps any element with a small annotation badge that
 * appears in the corner. The badge expands on hover/click to reveal a
 * full explanation: WHO uses it, WHAT happens after submit, WHERE data
 * flows, and WHO gets alerted.
 *
 * Props:
 *   tag       — short label shown in the badge ("01", "ROUTES", "FIELD")
 *   title     — bold callout title
 *   children  — UI being annotated (rendered inside)
 *   role      — "Foreman", "PM", etc.
 *   routes    — list of destinations the action flows to
 *   alerts    — list of who gets notified
 *   color     — accent color (default orange)
 *   anchor    — corner placement: 'tl' | 'tr' | 'bl' | 'br'
 */
export default function DemoCallout({
  tag = "DEMO",
  title,
  description,
  role,
  routes,
  alerts,
  color = "#FF6A00",
  anchor = "tr",        // kept for API compat — only used to flip the expand-panel side
  children,
  className = "",
  testId,
}) {
  const enabled = useCallouts();
  const [open, setOpen] = useState(false);

  if (!enabled) return <>{children}</>;

  // Expandable panel side — derived from the legacy `anchor` prop so
  // existing usages don't need touched. `tr`/`br` → panel hangs right;
  // `tl`/`bl` → panel hangs left.
  const panelOnRight = anchor === "tr" || anchor === "br";

  return (
    <div
      className={`relative inline-flex items-center gap-2 flex-wrap ${className}`}
      data-testid={testId}
    >
      {children}
      {/* Sibling badge — sits NEXT TO the wrapped content, never on top.
          This eliminates the historical overlap with small inline pills. */}
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="relative z-30 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 rounded shadow-lg shrink-0"
        style={{
          background: color,
          color: "#0A0A0A",
          border: `1px solid ${color}`,
          fontWeight: 800,
        }}
        data-testid={testId ? `${testId}-callout` : "demo-callout-badge"}
      >
        <span className="inline-flex w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#0A0A0A" }} />
        {tag}
      </button>

      {/* Expandable panel — hangs from the badge, below it */}
      {open && (
        <div
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className={`absolute ${panelOnRight ? "right-0" : "left-0"} top-full mt-2 z-40 w-[280px] sm:w-[320px] p-4 rounded-lg shadow-2xl`}
          style={{
            background: "#0F0F0F",
            border: `1px solid ${color}55`,
            borderTop: `2px solid ${color}`,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color }}>{tag}</span>
            <span className="h-px flex-1" style={{ background: `${color}33` }} />
          </div>
          {title && (
            <div className="font-display text-sm font-black tracking-tight text-white mb-1.5 leading-tight">
              {title}
            </div>
          )}
          {description && (
            <div className="text-xs leading-relaxed mb-2.5" style={{ color: "#BDBDBD" }}>
              {description}
            </div>
          )}
          {role && (
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>Role</span>
              <span className="text-xs font-semibold text-white">{role}</span>
            </div>
          )}
          {routes && routes.length > 0 && (
            <div className="mb-1.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-1" style={{ color: "#8A8A8A" }}>Routes to</div>
              <div className="flex flex-wrap gap-1">
                {routes.map((r) => (
                  <span key={r} className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded" style={{ background: "#1A1A1A", color: "#FFFFFF", border: "1px solid #2A2A2A" }}>
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}
          {alerts && alerts.length > 0 && (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-1" style={{ color: "#8A8A8A" }}>Alerts</div>
              <div className="flex flex-wrap gap-1">
                {alerts.map((a) => (
                  <span key={a} className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
